import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { notificationApi, groupApi } from '../../api/guestList';
import { useAuthContext } from '../../context/AuthContext';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import SpinnerLoader from '../../components/Buttons/SpinnerLoader';
import { handleApiResponse, showApiMessage } from '../global/ApiMessageModal/ApiMessageModal';

const containsEmoji = (text) => {
  if (!text) return false;
  try {
    return /\p{Extended_Pictographic}/u.test(text);
  } catch (e) {
    return /[\uD800-\uDFFF]/.test(text);
  }
};

const TumaTaarifaModal = ({ show, onHide }) => {
  const { isAuthenticated, currentEvent } = useAuthContext();

  const [messageTypes, setMessageTypes] = useState({ sms: false, whatsapp: false });
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);

  const [guestsData, setGuestsData] = useState([]);
  const [guestsLoading, setGuestsLoading] = useState(false);
  const [credits, setCredits] = useState({ sms: 0, whatsapp: 0 });
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  useEffect(() => {
    if (show) {
      fetchGuests();
      fetchGroups();
    }
  }, [show]);

  const fetchGroups = async () => {
    try {
      const response = await groupApi.getGroups();
      const res = response?.data || response;
      let groupsData = [];
      if (res && res.data) {
        if (Array.isArray(res.data.data)) {
          groupsData = res.data.data;
        } else if (Array.isArray(res.data)) {
          groupsData = res.data;
        }
      } else if (Array.isArray(res)) {
        groupsData = res;
      }
      setGroups(groupsData);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    }
  };

  const groupOptions = useMemo(() => {
    return groups.map(group => ({
      value: group.id,
      label: group.group_name
    }));
  }, [groups]);

  const fetchGuests = async () => {
    setGuestsLoading(true);
    try {
      const response = await notificationApi.getGuestsForNotifications({ event_id: currentEvent?.id });
      const responseData = response?.data?.data || response?.data || {};
      const guests = responseData.guests || [];

      if (responseData.message_template) {
        setMessageText(responseData.message_template);
      }

      setCredits({
        sms: responseData.sms_credits || 0,
        whatsapp: responseData.whatsapp_credits || 0
      });

      const formattedGuests = Array.isArray(guests) ? guests.map(guest => ({
        id: guest.id,
        name: guest.full_name || guest.name,
        phone: guest.mobile || guest.phone || guest.phone_number,
      })) : [];

      setGuestsData(formattedGuests);
    } catch (err) {
      console.error('Error fetching guests:', err);
      setGuestsData([]);
    } finally {
      setGuestsLoading(false);
    }
  };

  // Map guests to their groups from the groups query members list
  const guestsWithGroups = useMemo(() => {
    return guestsData.map(guest => {
      const guestGroups = groups
        .filter(group => Array.isArray(group.members) && group.members.some(member => member.id === guest.id))
        .map(group => ({
          id: group.id,
          group_name: group.group_name
        }));
      return {
        ...guest,
        groups: guestGroups
      };
    });
  }, [guestsData, groups]);

  const filteredGuests = useMemo(() => {
    return guestsWithGroups.filter(guest => {
      const matchesSearch = guest.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guest.phone?.includes(searchTerm);
      if (selectedGroups && selectedGroups.length > 0) {
        const selectedGroupIds = selectedGroups.map(g => g.value);
        const guestGroupIds = guest.groups?.map(g => g.id) || [];
        const matchesGroup = guestGroupIds.some(id => selectedGroupIds.includes(id));
        return matchesSearch && matchesGroup;
      }
      return matchesSearch;
    });
  }, [guestsWithGroups, searchTerm, selectedGroups]);

  const toggleContactSelection = (id) => {
    setSelectedGuests(prev =>
      prev.includes(id)
        ? prev.filter(guestId => guestId !== id)
        : [...prev, id]
    );
  };

  const selectAllFiltered = (e) => {
    if (e.target.checked) {
      setSelectedGuests(filteredGuests.map(g => g.id));
    } else {
      setSelectedGuests([]);
    }
  };

  const columns = [
    {
      name: (
        <div onClick={(e) => e.stopPropagation()}>
          <Form.Check
            type="checkbox"
            checked={filteredGuests.length > 0 && selectedGuests.length === filteredGuests.length}
            onChange={selectAllFiltered}
          />
        </div>
      ),
      width: '60px',
      cell: row => (
        <div onClick={(e) => e.stopPropagation()}>
          <Form.Check
            type="checkbox"
            checked={selectedGuests.includes(row.id)}
            onChange={() => toggleContactSelection(row.id)}
          />
        </div>
      )
    },
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      grow: 2,
      cell: row => {
        const groupNames = Array.isArray(row.groups) && row.groups.length > 0
          ? row.groups.map(g => g.group_name || g).join(', ')
          : '';
        return (
          <div
            className="d-flex align-items-center gap-3 py-2 w-100 h-100"
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              toggleContactSelection(row.id);
            }}
          >
            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 d-none d-sm-flex" style={{ width: '28px', height: '28px' }}>
              <i className="bi bi-person-fill" style={{ fontSize: '0.85rem' }}></i>
            </div>
            <div>
              <span className="text-dark d-block" style={{ fontSize: '0.85rem' }}>{row.name}</span>
              {groupNames && <small className="text-muted" style={{ fontSize: '0.75rem' }}>Group: {groupNames}</small>}
            </div>
          </div>
        );
      }
    },
    {
      name: 'Phone',
      selector: row => row.phone,
      sortable: true,
      cell: row => (
        <span
          className="text-dark d-block w-100 h-100 py-2"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            toggleContactSelection(row.id);
          }}
        >
          {row.phone || 'N/A'}
        </span>
      )
    }
  ];

  const handleSendNotification = async () => {
    if (!messageTypes.sms && !messageTypes.whatsapp) {
      toast.warning('Please select at least one sending method');
      return;
    }

    if (messageTypes.sms && containsEmoji(messageText)) {
      toast.error('SMS haitumi emoji. Tafadhali ondoa emoji au tumia Whatsapp pekee.');
      return;
    }

    if (selectedGuests.length === 0) {
      toast.warning('Please select at least one guest');
      return;
    }

    setSendingMessage(true);

    try {
      const payload = {
        event_id: currentEvent?.id,
        guest_ids: selectedGuests,
        message: messageText,
        send_sms: messageTypes.sms,
        send_whatsapp: messageTypes.whatsapp
      };

      const response = await notificationApi.sendNotifications(payload);
      handleApiResponse(response);

      onHide();
      setSelectedGuests([]);
    } catch (err) {
      console.error('Error sending notification:', err);
      if (err.response?.data) {
        handleApiResponse(err.response.data);
      } else if (err.data) {
        handleApiResponse(err.data);
      } else {
        showApiMessage({ success: false, status: 400, message: err.message || 'Failed to send notifications' });
      }
    } finally {
      setSendingMessage(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered contentClassName="border-0 shadow-lg">
      <Modal.Body className="bg-white p-4 p-md-5 position-relative rounded">
        {/* Close Button */}
        <button type="button" className="btn-close position-absolute top-0 end-0 m-3" onClick={onHide} aria-label="Close"></button>

        {/* Header */}
        <div className="text-center mb-4">
          <h5 className="fw-normal" style={{ color: '#7a8bb0' }}>Tuma Taarifa</h5>
          <div className={messageText.length > 160 ? "text-danger" : "text-muted"} style={{ fontSize: '0.85rem' }}>
            {messageText.length} Characters {Math.max(1, Math.ceil(messageText.length / 160))} Credits per message
          </div>
          {messageTypes.sms && containsEmoji(messageText) && (
            <div className="text-danger mt-1 fw-bold" style={{ fontSize: '0.85rem' }}>
              SMS haitumi emoji. Tafadhali ondoa emoji au tumia Whatsapp pekee.
            </div>
          )}
          <div className="d-flex justify-content-center gap-4 mt-3">
            <Form.Check
              type="checkbox"
              id="sms-check-taarifa"
              label={<span className="text-secondary" style={{ fontSize: '0.9rem' }}><i className="bi bi-envelope me-1"></i> SMS ({credits.sms})</span>}
              checked={messageTypes.sms}
              onChange={e => setMessageTypes({ ...messageTypes, sms: e.target.checked })}
            />
            <Form.Check
              type="checkbox"
              id="wa-check-taarifa"
              label={<span className="text-secondary" style={{ fontSize: '0.9rem' }}><FaWhatsapp className="me-1" /> Whatsapp ({credits.whatsapp})</span>}
              checked={messageTypes.whatsapp}
              onChange={e => setMessageTypes({ ...messageTypes, whatsapp: e.target.checked })}
            />
          </div>
        </div>

        {/* Textarea */}
        <Form.Control
          as="textarea"
          rows={5}
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          className="mb-4 shadow-sm"
          placeholder="Habari #jina, tunawataarifu kuhusu..."
          style={{ resize: 'none', borderColor: '#c1cbe0', color: '#495057', borderRadius: '8px', fontSize: '0.9rem' }}
        />

        {/* Table Section */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <h6 className="fw-semibold mb-0" style={{ color: '#475569' }}>Wageni Wote</h6>
              {selectedGuests.length > 0 && (
                <span className="badge bg-primary rounded-pill px-2 py-1" style={{ fontSize: '0.75rem' }}>
                  {selectedGuests.length} Selected
                </span>
              )}
            </div>

            <label className="d-flex align-items-center gap-1 text-secondary cursor-pointer mb-0" style={{ fontSize: '0.82rem', fontWeight: '500' }}>
              <input
                type="checkbox"
                id="select-all-taarifa"
                className="form-check-input"
                checked={filteredGuests.length > 0 && selectedGuests.length === filteredGuests.length}
                onChange={selectAllFiltered}
              />
              Select All
            </label>
          </div>

          <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch justify-content-between">
            <div style={{ flex: 1, minWidth: '180px' }}>
              <Select
                isMulti
                options={groupOptions}
                value={selectedGroups}
                onChange={setSelectedGroups}
                placeholder="Vikundi Vyote"
                className="basic-multi-select"
                classNamePrefix="select"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderRadius: '20px',
                    borderColor: state.isFocused ? '#86b7fe' : '#dee2e8',
                    fontSize: '0.82rem',
                    minHeight: '38px',
                    height: '38px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    backgroundColor: '#ffffff',
                    paddingLeft: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#cbd5e1'
                    }
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 8px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    overflow: 'hidden'
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: '36px'
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    padding: '4px'
                  }),
                  clearIndicator: (base) => ({
                    ...base,
                    padding: '4px'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#6c757d'
                  }),
                  multiValue: (base) => ({
                    ...base,
                    borderRadius: '12px',
                    backgroundColor: '#e2e8f0',
                    fontSize: '0.75rem'
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                    borderRadius: '12px',
                    fontSize: '0.82rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  })
                }}
              />
            </div>

            <div style={{ flex: 1, minWidth: '180px', maxWidth: '100%' }}>
              <Form.Control
                type="text"
                placeholder="Search guest..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="shadow-sm border"
                style={{
                  borderRadius: '20px',
                  padding: '0.375rem 1rem',
                  fontSize: '0.82rem',
                  height: '38px',
                  borderColor: '#dee2e8',
                  backgroundColor: '#ffffff'
                }}
              />
            </div>
          </div>
        </div>

        <div className="border-0">
          <StyledDataTable
            columns={columns}
            data={filteredGuests}
            progressPending={guestsLoading}
            progressComponent={<SpinnerLoader />}
            pagination={false}
            maxHeight="350px"
            noHeader
            responsive={false}
            onRowClicked={(row) => toggleContactSelection(row.id)}
            pointerOnHover
          />
        </div>

        {/* Floating Send Button */}
        <div className="mt-4 d-flex justify-content-start">
          <Button
            variant="primary"
            className="px-4 py-2 shadow"
            style={{ borderRadius: '8px', fontWeight: '500' }}
            onClick={handleSendNotification}
            disabled={sendingMessage || selectedGuests.length === 0 || !messageText.trim() || (messageTypes.sms && containsEmoji(messageText))}
          >
            {sendingMessage ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TumaTaarifaModal;
